import { useCallback, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { CloudOff } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { authService } from '../../services/auth'

interface ProtectedRouteProps {
  children: React.ReactNode
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isLoading, setUser, setLoading } = useAuthStore()
  // The sign-in check failed for a reason other than "signed out" (e.g. 503).
  const [unavailable, setUnavailable] = useState(false)

  const checkAuth = useCallback(async (retry = false) => {
    if (retry) {
      // Only on "Try again": a routine check must not flash the page.
      setUnavailable(false)
      setLoading(true)
    }
    try {
      setUser(await authService.checkAuth())
    } catch {
      setUnavailable(true)
      setLoading(false)
    }
  }, [setUser, setLoading])

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  const centered: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'var(--bg)',
    color: 'var(--text)',
  }

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div style={centered}>
        <div>Loading...</div>
      </div>
    )
  }

  // Couldn't verify the sign-in (service unavailable): never treat it as signed out
  // or signed in - offer a retry.
  if (unavailable) {
    return (
      <div style={centered}>
        <div style={{ textAlign: 'center', maxWidth: '360px', padding: '24px' }}>
          <CloudOff size={36} style={{ color: 'var(--text-muted)', marginBottom: '12px' }} />
          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '6px' }}>
            Pearson is temporarily unavailable
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>
            We couldn't check your sign-in. Please try again in a moment.
          </div>
          <button
            type="button"
            onClick={() => checkAuth(true)}
            style={{
              padding: '8px 16px',
              background: 'var(--accent)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Render protected content
  return <>{children}</>
}
